import api from "../../../utils/api";
import type { FirestoreTimestamp } from "./leadsService";

// ============================================================================
// CRM Calls — frontend service. Talks to the torre-de-control-api crmCalls module.
//   • list            GET  /crm/callsList
//   • single          GET  /crm/calls?id=
//   • place AI call   POST /crm/calls/place      { leadId, workflowId? }
//   • log human call  POST /crm/calls/manual
//   • delete          DELETE /crm/calls?id=
//   • refresh rec.    GET  /crm/calls/recording?id=
//   • contact summary POST /crm/contacts/ai-summary  { leadId?, phone?, contact? }
// The shared `api` axios client injects auth token + session headers.
// ============================================================================

export type CallerType = "ai" | "human";

export type CallStatus =
  | "queued"
  | "initiated"
  | "in_progress"
  | "completed"
  | "failed"
  | "no_answer"
  | "skipped";

export interface TranscriptSegment {
  role: "agent" | "user";
  text: string;
  timeInCallSecs: number | null;
}

export interface CallQualification {
  dataAccurate: boolean;
  corrections: Record<string, string | null>;
  recommendedStatus: string;
  score: number;
  summary: string;
  reasoning: string;
  qualifiedAt?: string;
}

export interface Call {
  id: string;
  leadId: string | null;
  contactId: string | null;
  workflowId: string | null;
  direction: "outbound" | "inbound";
  callerType: CallerType;
  provider: string;
  agentId: string | null;
  conversationId: string | null;
  providerCallId: string | null;
  to: string | null;
  from: string | null;
  status: CallStatus;
  statusDetail: string | null;
  transcript: string | null;
  transcriptSegments: TranscriptSegment[];
  summary: string | null;
  recordingUrl: string | null;
  recordingPath: string | null;
  durationSecs: number | null;
  qualification: CallQualification | null;
  costs?: { langfuseTraceId?: string | null } & Record<string, unknown>;
  triggeredBy: string | null;
  startedAt?: FirestoreTimestamp | string | null;
  endedAt?: FirestoreTimestamp | string | null;
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
}

export interface CallsPage {
  data: Call[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

export interface CallsListParams {
  page?: number;
  limit?: number;
  leadId?: string;
  contactId?: string;
  callerType?: CallerType;
  status?: CallStatus;
}

export async function fetchCalls(params: CallsListParams = {}): Promise<CallsPage> {
  const { page = 1, limit = 100, ...rest } = params;
  const res = await api.get("/crm/callsList", { params: { page, limit, ...rest } });
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

export async function getCall(id: string): Promise<Call | null> {
  const res = await api.get("/crm/calls", { params: { id } });
  return res.data?.data?.data ?? null;
}

export async function placeAiCall(leadId: string, crmAgentId?: string, workflowId?: string): Promise<Call> {
  const res = await api.post("/crm/calls/place", { leadId, crmAgentId, workflowId });
  return res.data?.data?.data;
}

/**
 * Place an AI call to a PROSPECT COMPANY (cold call, no lead yet). The chosen AI
 * agent introduces a product and tries to capture a contact; when it does, the
 * backend creates a lead from it (flagged so it is NOT auto-dialed). Returns the
 * created call doc.
 */
export async function placeCompanyAiCall(companyId: string, crmAgentId?: string): Promise<Call> {
  const res = await api.post("/crm/calls/place-company", { companyId, crmAgentId });
  return res.data?.data?.data;
}

// --- "Calls to do today" + bulk calling -------------------------------------

/** A new lead the AI agent still has to call (the call backlog). */
export interface CallTodoItem {
  leadId: string;
  name: string | null;
  company: string | null;
  to: string | null;
  product: string | null;
  createdAt?: FirestoreTimestamp | string | null;
  /** Set once the lead is in the throttled queue. */
  queueStatus: "pending" | "dispatched" | null;
  /** True if a prior AI call exists (e.g. no-answer being retried). */
  attempted: boolean;
  /** Why the automatic lead.created AI call was skipped (e.g. "budget_exhausted"). */
  autoCallSkipped?: string | null;
}

export interface CallTodo {
  items: CallTodoItem[];
  /** Max simultaneous AI calls the dispatcher allows (server-configured). */
  maxConcurrent: number;
}

/** The agent's pending-call backlog: new leads with a phone and no AI call yet. */
export async function fetchCallTodo(): Promise<CallTodo> {
  const res = await api.get("/crm/calls/todo");
  const data = res.data?.data?.data ?? {};
  return {
    items: Array.isArray(data.items) ? data.items : [],
    maxConcurrent: data.maxConcurrent ?? 3,
  };
}

/** A prospect company the AI agent still has to cold-call (the company backlog). */
export interface CompanyCallTodoItem {
  companyId: string;
  name: string | null;
  to: string | null;
  industry: string | null;
  address: string | null;
  product: string | null;
  createdAt?: FirestoreTimestamp | string | null;
  /** Set once the company is in the throttled queue. */
  queueStatus: "pending" | "dispatched" | null;
  /** True if a prior AI call exists (e.g. no-answer being retried). */
  attempted: boolean;
}

export interface CompanyCallTodo {
  items: CompanyCallTodoItem[];
  /** Max simultaneous AI calls the dispatcher allows (shared with leads). */
  maxConcurrent: number;
}

/** The cold-call backlog: prospect companies with a phone and no contact yet. */
export async function fetchCompanyCallTodo(): Promise<CompanyCallTodo> {
  const res = await api.get("/crm/calls/company-todo");
  const data = res.data?.data?.data ?? {};
  return {
    items: Array.isArray(data.items) ? data.items : [],
    maxConcurrent: data.maxConcurrent ?? 3,
  };
}

export interface BulkCallResult {
  queued: number;
  skipped: number;
  errors: { leadId?: string; companyId?: string; reason: string }[];
}

/**
 * Trigger AI calls in bulk. The calls are ENQUEUED and placed a few at a time
 * by the backend dispatcher (one shared number), not all at once. Pass
 * `{ all: true }` to call every pending new lead, or `{ leadIds }` for a subset.
 */
export async function bulkPlaceAiCalls(input: {
  leadIds?: string[];
  all?: boolean;
  workflowId?: string;
}): Promise<BulkCallResult> {
  const res = await api.post("/crm/calls/bulk", input);
  return res.data?.data?.data ?? { queued: 0, skipped: 0, errors: [] };
}

/**
 * Trigger cold AI calls to prospect COMPANIES in bulk. Uses the same throttled
 * queue/dispatcher as lead bulk calling (one shared line) — enqueued, not all at
 * once. Pass `{ all: true }` for every pending company, or `{ companyIds }` for a
 * subset. `crmAgentId` picks the product-intro agent that runs the calls.
 */
export async function bulkPlaceCompanyAiCalls(input: {
  companyIds?: string[];
  all?: boolean;
  crmAgentId?: string;
}): Promise<BulkCallResult> {
  const res = await api.post("/crm/calls/bulk-company", input);
  return res.data?.data?.data ?? { queued: 0, skipped: 0, errors: [] };
}

export interface DispatchResult {
  dispatched: number;
  capacity: number;
  inFlight: number;
  /** True when the dispatcher ran outside the dial window (quiet hours). */
  outsideWindow?: boolean;
}

/**
 * Run the call-queue dispatcher now instead of waiting for the scheduled run.
 * Places up to the free capacity (cap − in-flight) of pending queued calls.
 */
export async function dispatchCallQueue(): Promise<DispatchResult> {
  const res = await api.post("/crm/calls/dispatch", {});
  return res.data?.data?.data ?? { dispatched: 0, capacity: 0, inFlight: 0 };
}

export interface DismissTodoResult {
  dismissed: number;
  dequeued: number;
  errors: { leadId?: string; reason: string }[];
}

/**
 * Remove lead(s) from the "Por llamar" backlog: cancels any queued call and
 * hides the lead from the to-call list (the lead itself is kept). Reversible
 * with `restoreCallTodo`. Used by the manual single + batch delete.
 */
export async function dismissCallTodo(leadIds: string[]): Promise<DismissTodoResult> {
  const res = await api.post("/crm/calls/todo/dismiss", { leadIds });
  return res.data?.data?.data ?? { dismissed: 0, dequeued: 0, errors: [] };
}

/** Undo a dismissal: the lead reappears in the backlog (un-queued). */
export async function restoreCallTodo(leadIds: string[]): Promise<{ restored: number }> {
  const res = await api.post("/crm/calls/todo/restore", { leadIds });
  return res.data?.data?.data ?? { restored: 0 };
}

// --- Human softphone (Twilio Voice WebRTC) ----------------------------------

export interface VoiceTokenResult {
  token: string;
  identity: string;
  expiresInSeconds: number;
}

/** Mint a Twilio Voice access token for booting the browser softphone Device. */
export async function getVoiceToken(): Promise<VoiceTokenResult> {
  const res = await api.get("/crm/voice/token");
  return res.data?.data?.data;
}

export interface HumanCallStart {
  callId: string;
  contactName: string | null;
  to: string | null;
}

/**
 * Open a HUMAN call record before dialing from the browser. Returns the callId
 * the softphone passes to Device.connect({ params: { callId } }) — the backend
 * TwiML webhook resolves the lead's phone from it (the number never leaves the
 * server to the browser).
 */
export async function startHumanCall(leadId: string, crmAgentId?: string): Promise<HumanCallStart> {
  const res = await api.post("/crm/calls/human/start", { leadId, crmAgentId });
  return res.data?.data?.data;
}

export interface ManualCallInput {
  leadId?: string;
  contactId?: string;
  to?: string;
  durationSecs?: number;
  notes?: string;
  outcome?: string;
  direction?: "outbound" | "inbound";
}

export async function createManualCall(input: ManualCallInput): Promise<Call> {
  const res = await api.post("/crm/calls/manual", input);
  return res.data?.data?.data;
}

export async function deleteCall(id: string): Promise<void> {
  await api.delete("/crm/calls", { params: { id } });
}

export async function refreshRecordingUrl(id: string): Promise<string | null> {
  const res = await api.get("/crm/calls/recording", { params: { id } });
  return res.data?.data?.data?.url ?? null;
}

// --- Contact AI summary -----------------------------------------------------
export interface ContactSummaryPerCall {
  callId: string;
  summary: string;
  keyPoints: string[];
  sentiment: "positivo" | "neutral" | "negativo";
}

export interface ContactSummary {
  perCall: ContactSummaryPerCall[];
  overall: string;
  nextStep: string;
}

export interface ContactSummaryResult {
  summary: ContactSummary | null;
  callsConsidered: number;
  callIds?: string[];
  langfuseTraceId?: string | null;
}

export async function getContactSummary(input: {
  leadId?: string;
  phone?: string;
  contact?: Record<string, unknown>;
}): Promise<ContactSummaryResult> {
  const res = await api.post("/crm/contacts/ai-summary", input);
  const data = res.data?.data?.data ?? {};
  return {
    summary: data.summary ?? null,
    callsConsidered: data.callsConsidered ?? 0,
    callIds: data.callIds,
    langfuseTraceId: data.langfuseTraceId,
  };
}

// --- helpers ----------------------------------------------------------------
export const CALL_STATUS_META: Record<CallStatus, { label: string; tone: string }> = {
  queued: { label: "En cola", tone: "bg-gray-400/15 text-gray-500" },
  initiated: { label: "Iniciada", tone: "bg-sky-500/15 text-sky-500" },
  in_progress: { label: "En curso", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  completed: { label: "Completada", tone: "bg-emerald-500/15 text-emerald-500" },
  failed: { label: "Fallida", tone: "bg-red-500/15 text-red-500" },
  no_answer: { label: "Sin respuesta", tone: "bg-gray-400/15 text-gray-500" },
  skipped: { label: "Omitida", tone: "bg-gray-400/15 text-gray-500" },
};

export function fmtDuration(secs: number | null | undefined): string {
  if (secs == null || isNaN(secs)) return "—";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}m ${String(s).padStart(2, "0")}s`;
}
